import { safeApiRequest, apiRequest } from './apiClient';
import { sanitizeSearchInput, sanitizeUserName } from '../utils/security';

const USERS_ENDPOINT = '/api/users';
const USER_STATS_ENDPOINT = '/api/users/stats';

const normalizeRole = (role) => {
  const safeRole = sanitizeSearchInput(role || '');
  const allowedRoles = ['admin', 'mod', 'sales', 'sevkiyat', 'tech-service', 'tech-person', 'bayi', 'review-app'];
  return allowedRoles.includes(safeRole) ? safeRole : undefined;
};

/**
 * Kullanıcı payload'ını oluşturur
 * @param {Object} payload - Kullanıcı bilgileri
 * @param {boolean} includeAktif - aktif alanını dahil et (default: true, create için false olmalı)
 * @returns {Object} Sanitize edilmiş payload
 */
const buildUserPayload = (payload = {}, includeAktif = true) => {
  const data = {};

  if (payload.kullaniciAdi !== undefined && payload.kullaniciAdi !== '') {
    data.kullaniciAdi = sanitizeUserName(payload.kullaniciAdi);
  }

  if (payload.sifre !== undefined && payload.sifre !== '') {
    data.sifre = String(payload.sifre).trim();
  }

  if (payload.mail !== undefined && payload.mail !== '') {
    data.mail = String(payload.mail).trim().toLowerCase();
  }

  if (payload.telefon !== undefined && payload.telefon !== '') {
    data.telefon = String(payload.telefon).trim();
  }

  if (payload.ad !== undefined) {
    const trimmedAd = payload.ad ? String(payload.ad).trim() : '';
    // Boş string ise null gönder, aksi halde trimmed değeri gönder
    data.ad = trimmedAd === '' ? null : trimmedAd;
  }

  if (payload.soyad !== undefined) {
    const trimmedSoyad = payload.soyad ? String(payload.soyad).trim() : '';
    // Boş string ise null gönder, aksi halde trimmed değeri gönder
    data.soyad = trimmedSoyad === '' ? null : trimmedSoyad;
  }

  if (payload.rol !== undefined) {
    const role = normalizeRole(payload.rol);
    if (role) {
      data.rol = role;
    }
  }

  // TechService - dokümantasyona göre MongoDB ObjectId veya null
  if (payload.techService !== undefined) {
    const raw = payload.techService;
    if (raw === null || raw === '') {
      data.techService = null;
    } else {
      const s = String(raw).trim();
      data.techService = s || null;
    }
  }

  // Aktif - sadece update için gönder (create endpoint'i /api/auth/kayit bunu kabul etmiyor)
  if (includeAktif && payload.aktif !== undefined) {
    data.aktif = Boolean(payload.aktif);
  }

  if (payload.isTest !== undefined) {
    data.isTest = payload.isTest === true;
  }

  if (payload.permissions !== undefined && Array.isArray(payload.permissions)) {
    data.permissions = payload.permissions.filter(p => p && p.trim() !== '');
  }

  if (payload.orderCreationPermission !== undefined) {
    data.orderCreationPermission = payload.orderCreationPermission;
  }

  return data;
};

export const fetchUsers = async ({ page = 1, limit = 10, rol, aktif, search } = {}) => {
  const queryParams = {
    sayfa: page,
    limit,
    rol: normalizeRole(rol),
    aktif,
  };
  
  console.log('[fetchUsers] Query params:', queryParams);
  
  return safeApiRequest(USERS_ENDPOINT, {
    method: 'GET',
    query: queryParams,
  });
};

/**
 * Kullanıcı arama endpoint'i
 * @param {string} arama - Arama terimi (opsiyonel, min 3 karakter, kullaniciAdi, ad, soyad alanlarında aranır)
 * @param {Object} options - Diğer seçenekler
 * @param {boolean} options.aktif - Aktif durum filtresi (opsiyonel)
 * @param {string} options.rol - Rol filtresi (opsiyonel)
 * @param {number} options.page - Sayfa numarası
 * @param {number} options.limit - Sayfa başına kayıt sayısı (max 100)
 * @note En az bir arama kriteri zorunludur (arama veya aktif)
 */
export const searchUsers = async (arama, { aktif, rol, page = 1, limit = 10, genis = false } = {}) => {
  const queryParams = {
    sayfa: page,
    limit: Math.min(limit, 100), // Maksimum 100
  };

  const minLen = genis ? 2 : 3;
  if (arama && arama.trim().length >= minLen) {
    queryParams.arama = sanitizeSearchInput(arama.trim());
  }

  if (genis) {
    queryParams.genis = 'true';
  }

  // Aktif parametresi ekle
  if (aktif !== undefined) {
    queryParams.aktif = aktif === true ? 'true' : 'false';
  }

  const roleParam = normalizeRole(rol);
  if (roleParam) {
    queryParams.rol = roleParam;
  }

  // En az bir arama kriteri kontrolü (backend gereksinimi)
  if (!queryParams.arama && queryParams.aktif === undefined) {
    throw new Error('En az bir arama kriteri belirtilmelidir (arama veya aktif)');
  }
  
  console.log('[searchUsers] Query params:', queryParams);
  
  return safeApiRequest(`${USERS_ENDPOINT}/search`, {
    method: 'GET',
    query: queryParams,
  });
};

export const fetchUserById = async (userId) => {
  if (!userId) {
    throw new Error('Kullanıcı ID bilgisi gerekli');
  }

  return safeApiRequest(`${USERS_ENDPOINT}/${userId}`, {
    method: 'GET',
  });
};

/**
 * Kullanıcı oluşturur
 * Backend dokümantasyonuna göre: POST /api/auth/kayit (public endpoint, token gerektirmez)
 * @param {Object} payload - Kullanıcı bilgileri
 * @returns {Promise<Object>} Kullanıcı oluşturma sonucu
 */
export const createUser = async (payload) => {
  // Dokümantasyona göre endpoint: /api/auth/kayit (public endpoint)
  const CREATE_USER_ENDPOINT = '/api/auth/kayit';
  
  // create endpoint'i aktif alanını kabul etmiyor, includeAktif: false
  const sanitizedPayload = buildUserPayload(payload, false);
  console.log('[UsersService] Creating user:', {
    endpoint: CREATE_USER_ENDPOINT,
    method: 'POST',
    payload: sanitizedPayload
  });
  
  try {
    // Public endpoint olduğu için safeApiRequest yerine apiRequest kullanıyoruz
    // (safeApiRequest token gerektirir, ama bu endpoint public)
    const response = await apiRequest(CREATE_USER_ENDPOINT, {
      method: 'POST',
      body: sanitizedPayload,
      skipCsrf: false, // CSRF token gerekebilir
    });
    
    console.log('[UsersService] Create user response:', response);
    return response;
  } catch (error) {
    console.error('[UsersService] Create user error:', {
      endpoint: CREATE_USER_ENDPOINT,
      method: 'POST',
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

export const updateUser = async (userId, payload) => {
  const builtPayload = buildUserPayload(payload);
  console.log('[UsersService] updateUser - Original payload:', payload);
  console.log('[UsersService] updateUser - Built payload:', builtPayload);
  console.log('[UsersService] updateUser - User ID:', userId);
  try {
    const response = await safeApiRequest(`${USERS_ENDPOINT}/${userId}`, {
      method: 'PUT',
      body: builtPayload,
    });
    console.log('[UsersService] updateUser - API Response:', response);
    return response;
  } catch (error) {
    console.error('[UsersService] updateUser - API Error:', {
      status: error.status,
      message: error.message,
      data: error.data,
    });
    throw error;
  }
};

export const deleteUser = async (userId) => {
  return safeApiRequest(`${USERS_ENDPOINT}/${userId}`, {
    method: 'DELETE',
  });
};

export const fetchUserStats = async () => {
  return safeApiRequest(USER_STATS_ENDPOINT, {
    method: 'GET',
  });
};


